import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isActive?: boolean;
}

interface NavigationContextType {
  breadcrumbs: BreadcrumbItem[];
  currentPage: string;
  setBreadcrumbs?: (breadcrumbs: BreadcrumbItem[]) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const location = useLocation();
  
  const breadcrumbs = useMemo(() => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    if (segments.length === 0) {
      return [{ label: 'Home', href: '/', isActive: true }];
    }

    const breadcrumbItems: BreadcrumbItem[] = [
      { label: 'Home', href: '/' }
    ];

    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      
      breadcrumbItems.push({
        label: formatSegmentLabel(segment),
        href: isLast ? undefined : currentPath,
        isActive: isLast
      });
    });

    return breadcrumbItems;
  }, [location.pathname]);

  const currentPage = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments.length > 0 ? formatSegmentLabel(segments[segments.length - 1]) : 'Home';
  }, [location.pathname]);

  const value = useMemo(() => ({
    breadcrumbs,
    currentPage,
  }), [breadcrumbs, currentPage]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

function formatSegmentLabel(segment: string): string {
  // Handle special cases
  const labelMap: Record<string, string> = {
    'dashboard': 'Dashboard',
    'company': 'Company',
    'valuations': 'Valuations',
    'plan': 'Plan',
    'documents': 'Documents',
    'tasks': 'Tasks',
    'team': 'Team',
    'learn': 'Learn',
    'settings': 'Settings',
    'assessment': 'Getting Started',
    'profile': 'Profile',
  };

  return labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
}